import { useNavigate, useSearch } from '@tanstack/react-router';
import type { ComponentType, JSX } from 'react';
import { Page, PageHeader } from '@/components/layout/page.js';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs.js';
import {
  isSubscriptionView,
  readSubscriptionView,
  SUBSCRIPTION_VIEW_LABELS,
  SUBSCRIPTION_VIEWS,
  type SubscriptionView,
} from './views.js';
import { CantonFixesTab } from './canton-fixes-tab.js';
import { ChargeFailuresTab } from './charge-failures-tab.js';
import { ExportsTab } from './exports-tab.js';
import { KlaviyoTab } from './klaviyo-tab.js';
import { MigrationsTab } from './migrations-tab.js';
import { WebhooksTab } from './webhooks-tab.js';

/** Un composant par onglet déclaré dans `views.ts` — le typage refuse un onglet sans écran. */
const SUBSCRIPTION_TAB_COMPONENTS: Readonly<Record<SubscriptionView, ComponentType>> = {
  paiements: ChargeFailuresTab,
  cantons: CantonFixesTab,
  migrations: MigrationsTab,
  exports: ExportsTab,
  klaviyo: KlaviyoTab,
  webhooks: WebhooksTab,
};

/**
 * Écran Abonnements (`06` §8) — suivi des automatisations Recharge, un
 * onglet par fonction porté par `?view=`. Changer d'onglet repart d'une URL
 * propre (filtres, page, tri et panneau sont propres à chaque onglet).
 */
export function SubscriptionsPage(): JSX.Element {
  const rawSearch: unknown = useSearch({ strict: false });
  const navigate = useNavigate();
  const view = readSubscriptionView(rawSearch);
  const TabComponent = SUBSCRIPTION_TAB_COMPONENTS[view];

  function setView(next: SubscriptionView): void {
    void navigate({ to: '.', search: { view: next }, replace: true });
  }

  return (
    <Page width="full">
      <PageHeader title="Abonnements" description="Suivi des automatisations Recharge" />

      <Tabs
        value={view}
        onValueChange={(next) => {
          if (isSubscriptionView(next)) {
            setView(next);
          }
        }}
      >
        <TabsList>
          {SUBSCRIPTION_VIEWS.map((entry) => (
            <TabsTrigger key={entry} value={entry}>
              {SUBSCRIPTION_VIEW_LABELS[entry]}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <TabComponent />
    </Page>
  );
}
